import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from '@/app/providers/AuthProvider';
import { useDecedentQuery } from '@/entities/decedent';

const DecedentContext = createContext(null);

const DecedentProvider = ({ children }) => {
  const { user, isAuth, isLoggingOut } = useAuth();
  const [currentDecedentId, setCurrentDecedentId] = useState(null);
  const userDecedents = user?.decedents || [];

  useEffect(() => {
    if (!isAuth || isLoggingOut) {
      setCurrentDecedentId(null);
      return;
    }

    if (!userDecedents.length) {
      setCurrentDecedentId(null);
      return;
    }

    setCurrentDecedentId((prev) => {
      if (prev && userDecedents.some(({ id }) => id === prev)) {
        return prev;
      }

      return userDecedents[0].id;
    });
  }, [isAuth, isLoggingOut, userDecedents]);

  const currentDecedentQuery = useDecedentQuery(currentDecedentId, {
    enabled: !!currentDecedentId && isAuth && !isLoggingOut,
  });

  const current = currentDecedentQuery.data || null;
  const sections = current?.sections;

  const value = useMemo(
    () => ({
      currentDecedentId,
      setCurrentDecedentId,
      userDecedents,
      currentDecedent: current?.decedent || null,
      currentPrimaryUser: current?.primaryUser || null,
      currentRole: current?.role || null,
      sections,
      passedBasicForm: sections?.basic_document_submission?.question?.passed,
      finishedClosingTasks: sections?.closing?.task?.passed,
      isCurrentDecedentLoading: currentDecedentQuery.isPending,
      currentDecedentQuery,
    }),
    [current, currentDecedentId, currentDecedentQuery, sections, userDecedents]
  );

  return (
    <DecedentContext.Provider value={value}>
      {children}
    </DecedentContext.Provider>
  );
};

const useDecedentContext = () => {
  const context = useContext(DecedentContext);

  if (!context) {
    throw new Error('useDecedentContext must be used within DecedentProvider');
  }

  return context;
};

export const useCurrentDecedentId = () =>
  useDecedentContext().currentDecedentId;
export const useCurrentDecedent = () => useDecedentContext().currentDecedent;
export const useCurrentPrimaryUser = () =>
  useDecedentContext().currentPrimaryUser;
export const useCurrentDecedentRole = () => useDecedentContext().currentRole;
export const useCurrentDecedentSections = () => useDecedentContext().sections;
export const usePassedBasicForm = () => useDecedentContext().passedBasicForm;
export const useFinishedClosingTasks = () =>
  useDecedentContext().finishedClosingTasks;
export const useCurrentDecedentLoading = () =>
  useDecedentContext().isCurrentDecedentLoading;
export const useCurrentDecedentQueryState = () =>
  useDecedentContext().currentDecedentQuery;
export const useUserDecedents = () => useDecedentContext().userDecedents;
export const useSetCurrentDecedentId = () =>
  useDecedentContext().setCurrentDecedentId;

export default DecedentProvider;
